import { useCallback, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { DragOverlay, PointerSensor } from "@dnd-kit/core";
import { useSortable, type SortingStrategy } from "@dnd-kit/sortable";
import { Icon } from "../icons/Icon";
import { cx } from "../utils/format";

/** The parts the kit's two drags share (the flat `SortableList` and its nested mode `SortableGroups`, the sidebar editor rework, 2026-10-26):
 *  the keys, the no-slide strategy, the ghost and the insertion line, the handle, the pointer sensor that lets quick clicks through, and the
 *  holds that keep quick keys and Escape where they belong. */

export const KEYS = { start: ["Space"], cancel: ["Escape"], end: ["Space"] };
export const INSTRUCTIONS = "Press Space to pick this row up. Then the arrow keys move it, Space drops it and Escape puts it back.";
/** no slide: the other rows keep their places while one is carried */
export const stayInPlace: SortingStrategy = () => null;
export const PICK_UP_AFTER = 4; // pixels of pointer movement before a press becomes a drag (a click on the handle stays a click)
export const EDGE = 0.06; // the share of the scrolling box near its edges where holding the pointer scrolls the list
export type DropLine = { top: number; left: number; width: number } | null;

/** the ghost (a small box with the handle mark and the carried row's name, never a copy of the row) through dnd-kit's overlay, and the
 *  insertion line in the accent; nothing glides (no drop animation, no overlay transition) */
export function DragLayer({ carry, line }: { carry: { name: string; width: number } | null; line: DropLine }) {
  return createPortal(
    <>
      <DragOverlay dropAnimation={null} transition="none" className="ob-row-drag__overlay" style={{ width: "auto", height: "auto" }}>
        {carry ? <div className="ob-row-drag__ghost" aria-hidden="true" style={{ width: carry.width }}><Icon name="dragHandle" size="sm" /><span className="ob-row-drag__ghost-name">{carry.name}</span></div> : null}
      </DragOverlay>
      {line && <div className="ob-row-drag__line" data-drop-line="" aria-hidden="true" style={line} />}
    </>,
    document.body,
  );
}

/** dnd-kit's keyboard sensor starts hearing keys one timer tick after Space picked the row up, and the browser may handle quick keys first (a
 *  fast typist, a slow page), which were lost: the keys pressed in between are held from the pick-up and handed on once it listens */
export function holdEarlyKeys({ event }: { event: KeyboardEvent }): void {
  const doc = (event.target as Node).ownerDocument ?? document;
  const held: KeyboardEvent[] = [];
  const hold = (e: KeyboardEvent) => { if (e === event || !e.isTrusted) return; e.stopImmediatePropagation(); e.preventDefault(); held.push(e); };
  doc.addEventListener("keydown", hold, true);
  setTimeout(() => setTimeout(() => { // the sensor's own tick runs between the two
    doc.removeEventListener("keydown", hold, true);
    for (const e of held) (doc.activeElement ?? doc.body).dispatchEvent(new KeyboardEvent("keydown", { key: e.key, code: e.code, bubbles: true, cancelable: true }));
  }));
}

/** dnd-kit's pointer sensor, with its click swallow shortened (the sidebar editor rework, 2026-10-26). After a pointer drop dnd-kit swallows
 *  every click for 50 ms, meant for the click the release itself makes; a real click in that time (a quick person, a test ticking a box right
 *  after a drop) was lost, and the earlier fix held it for 60 ms and handed it on late, which a checkbox's own check reads as "nothing
 *  happened". Here the swallow ends right after the release's own click (it arrives in the same turn as the release), so a later click
 *  reaches its target at once. The swallow lives in the sensor's `documentListeners` (dnd-kit 6.3.1, pinned exactly). */
type SensorInternals = { detach: () => void; documentListeners?: { removeAll(): void } };
export class QuickClickPointerSensor extends PointerSensor {
  constructor(...args: ConstructorParameters<typeof PointerSensor>) {
    super(...args);
    const self = this as unknown as SensorInternals; // dnd-kit keeps both private; the instance's own `detach` wins over its prototype's
    const detach = self.detach.bind(this);
    self.detach = () => { detach(); const doc = self.documentListeners; if (doc) setTimeout(() => doc.removeAll()); };
  }
}

/** while a row is carried, Escape belongs to the drag: claimed in the capture phase, before a dialog's own listener, which ignores it then;
 *  dnd-kit's sensors still hear it and cancel the drag */
export function useEscapeClaim(dragging: boolean): void {
  useEffect(() => {
    if (!dragging) return;
    const claim = (e: KeyboardEvent) => { if (e.key === "Escape") e.preventDefault(); };
    window.addEventListener("keydown", claim, true);
    return () => window.removeEventListener("keydown", claim, true);
  }, [dragging]);
}

/** one row's handle: dnd-kit's sortable item, the handle being its activator and the row (the nearest element matching `row`, by default the
 *  nearest `rowProps` element) its measured box; `data` rides along on dnd-kit's item (the nested mode's kind) */
export function Handle({ id, name, rows, row = "[data-drag-row]", data }: { id: string; name: string; rows: Map<string, HTMLElement>; row?: string; data?: Record<string, unknown> }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({ id, data });
  const ref = useCallback((el: HTMLButtonElement | null) => {
    const box = el?.closest<HTMLElement>(row) ?? null;
    setActivatorNodeRef(el); setNodeRef(box);
    if (box) rows.set(id, box); else rows.delete(id);
  }, [id, rows, row, setActivatorNodeRef, setNodeRef]);
  return (
    <button type="button" ref={ref} className={cx("ob-row-drag__handle", isDragging && "ob-row-drag__handle--active")} aria-label={`Drag to reorder ${name}`}
      aria-describedby={attributes["aria-describedby"]} aria-pressed={isDragging} {...listeners}>
      <Icon name="dragHandle" size="sm" />
    </button>
  );
}
